import type {
	OrderCheckFiringLevel,
	OrderCheckLevel,
} from './order-check-level';
import { isOrderCheckOn, isOrderedQuantity } from './order-check-level';

/**
 * PD-16276 — the two per-line price floors: the item's minimum price and its
 * cost.
 *
 * Both callers resolve the inputs with the same @supply-mover-2-0/core
 * helpers (resolveEffectiveMinimumPrice, isPriceFromContract,
 * getApplicableContractCost), so a line priced at the contract price that
 * applies at its quantity is exempt in the CRM cart and on the server alike.
 */

export interface MinimumPriceLineModel {
	itemId: number;
	quantity: number;
	price: number;
	/**
	 * The minimum that applies to the line: the item's own minimum, else the
	 * default minimum when that setting applies, else none.
	 */
	minimumPrice: number | null | undefined;
	/** True when the line is priced at the contract price for its quantity. */
	priceMatchesContract: boolean;
}

export interface MinimumPriceFindingModel {
	itemId: number;
	level: OrderCheckFiringLevel;
	minimumPrice: number;
	price: number;
}

export function checkMinimumPrices(
	lines: ReadonlyArray<MinimumPriceLineModel>,
	level: OrderCheckLevel | null | undefined,
): Array<MinimumPriceFindingModel> {
	if (!isOrderCheckOn(level)) {
		return [];
	}

	const findings: Array<MinimumPriceFindingModel> = [];

	for (const line of lines) {
		const minimumPrice = line.minimumPrice;

		if (
			!isOrderedQuantity(line.quantity) ||
			minimumPrice == null ||
			!(minimumPrice > 0) ||
			!(line.price < minimumPrice) ||
			line.priceMatchesContract
		) {
			continue;
		}

		findings.push({
			itemId: line.itemId,
			level,
			minimumPrice,
			price: line.price,
		});
	}

	return findings;
}

export interface PriceBelowCostLineModel {
	itemId: number;
	quantity: number;
	price: number;
	/** The item's cost in the field the tenant chose (sell, acquisition or average). */
	cost: number | null | undefined;
	/** True when the line is priced at the contract price for its quantity. */
	priceMatchesContract: boolean;
	/**
	 * True when a contract cost applies to the item at the line's quantity.
	 * The contract cost is the cost the order locks in at submit, so the
	 * item's own stored cost is stale for it and must not raise a below-cost
	 * finding (PD-14364).
	 */
	onContractCost: boolean;
}

export interface PriceBelowCostFindingModel {
	itemId: number;
	level: OrderCheckFiringLevel;
	cost: number;
	price: number;
}

/**
 * A line priced at zero is not "below cost": free goods and samples are
 * priced at zero on purpose, and the CRM cart has never flagged them.
 */
export function checkPriceBelowCost(
	lines: ReadonlyArray<PriceBelowCostLineModel>,
	level: OrderCheckLevel | null | undefined,
): Array<PriceBelowCostFindingModel> {
	if (!isOrderCheckOn(level)) {
		return [];
	}

	const findings: Array<PriceBelowCostFindingModel> = [];

	for (const line of lines) {
		const cost = line.cost;

		if (
			!isOrderedQuantity(line.quantity) ||
			!(line.price > 0) ||
			cost == null ||
			!(cost > 0) ||
			!(line.price < cost) ||
			line.priceMatchesContract ||
			line.onContractCost
		) {
			continue;
		}

		findings.push({ itemId: line.itemId, level, cost, price: line.price });
	}

	return findings;
}
